import { isNumberString } from 'class-validator';

import {
  Enums,
  SharedModels,
  UIDtoFactory,
} from '@project44-models/iam-gateway';

import { UnifiedRolesEditorPolicy } from '../abstract/UnifiedRolesEditorPolicy';
import { TenantSpecificTenantAdminRolesEnum } from '../enums';
import {
  IRolesEditorPolicy,
  TUnifiedRoleCollectionDto,
  TUnifiedRolesMap,
} from '../interfaces';
import { UnifiedRolesEditor } from '../UnifiedRolesEditor';

export class UnifiedRolesEditorResolver implements UnifiedRolesEditorPolicy {
  public static readonly shouldDisableNMCRolesSupport = (
    featureFlags: Record<string, boolean>,
  ): boolean => {
    if (!!featureFlags)
      return featureFlags[
        UnifiedRolesEditorResolver.IAM_3542_DISABLE_UM_UI_NMC_ROLE_SUPPORT
      ];

    return false;
  };

  public checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole'];
  public checkUnifiedRoleIsTenantAdminUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsTenantAdminUnifiedRole'];
  public checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole: UnifiedRolesEditorPolicy['checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole'];

  constructor(
    private readonly payload: {
      unifiedRolesAssignedToResource: TUnifiedRoleCollectionDto;
      allAvailableUnifiedRoles: TUnifiedRoleCollectionDto;
      unifiedRolesEditor: UnifiedRolesEditor;
    },
  ) {
    this.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole;

    this.checkUnifiedRoleIsTenantAdminUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsTenantAdminUnifiedRole;

    this.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole =
      this.payload.unifiedRolesEditor.checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole;
  }

  public static create(payload: {
    unifiedRolesAssignedToResource: TUnifiedRoleCollectionDto;
    allAvailableUnifiedRoles: TUnifiedRoleCollectionDto;
    unifiedRolesEditor: UnifiedRolesEditor;
  }): UnifiedRolesEditorPolicy {
    return new UnifiedRolesEditorResolver(payload);
  }

  public readonly composeResourceTenantAgnosticRolesMap: IRolesEditorPolicy['composeResourceTenantAgnosticRolesMap'] =
    () => {
      return this.payload.unifiedRolesEditor.composeResourceTenantAgnosticRolesMap(
        {
          availableTenantAgnosticRolesCollection:
            this.payload.allAvailableUnifiedRoles,
          assignedTenantAgnosticRolesCollection:
            this.payload.unifiedRolesAssignedToResource,
        },
      );
    };

  public readonly composeResourceTenantSpecificRolesMap: IRolesEditorPolicy['composeResourceTenantSpecificRolesMap'] =
    (payload?: { featureFlags?: Record<string, boolean> }) => {
      const { featureFlags = {} } = payload ?? {};
      const tenantRoles =
        this.payload.allAvailableUnifiedRoles.toSerializedModel();

      return this.payload.unifiedRolesEditor.composeResourceTenantSpecificRolesMap(
        {
          availableTenantSpecificRoles: UIDtoFactory.create(
            Enums.UiDtoFactoryEnum.UNIFIED_ROLE_COLLECTION,
            this.handleNMCRolesSupport({ tenantRoles, featureFlags }),
          ),
          assignedTenantSpecificRoles:
            this.payload.unifiedRolesAssignedToResource,
        },
      );
    };

  public readonly composeUserTenantSpecificTenantAdminRoles: IRolesEditorPolicy['composeUserTenantSpecificTenantAdminRoles'] =
    (tenantSpecificTenantAdminRolesType, payload) => {
      const shouldDisableNMCRolesSupport =
        UnifiedRolesEditorResolver.shouldDisableNMCRolesSupport(
          payload?.featureFlags,
        );

      const {
        tenantAdminRole,
        nmcLegacyTenantAdminRole,
        nmcLegacyReadOnlyRole,
      } =
        this.payload.unifiedRolesEditor.getAllTenantSpecificAdministrationRoles(
          {
            tenantSpecificRoles: this.payload.allAvailableUnifiedRoles,
          },
        );

      const userTenantSpecificRoles =
        this.composeResourceTenantSpecificRolesMap();

      switch (tenantSpecificTenantAdminRolesType) {
        case TenantSpecificTenantAdminRolesEnum.WITH_TENANT_SPECIFIC_TENANT_ADMIN_ROLES:
          return this.batchUnifiedRoles({
            roles: userTenantSpecificRoles,
            rolesToAdd: shouldDisableNMCRolesSupport
              ? [tenantAdminRole]
              : [tenantAdminRole, nmcLegacyTenantAdminRole],
            rolesToRemove: [nmcLegacyReadOnlyRole],
          });
        case TenantSpecificTenantAdminRolesEnum.WITH_NON_TENANT_ADMIN_ROLES:
          return this.batchUnifiedRoles({
            roles: userTenantSpecificRoles,
            rolesToAdd: shouldDisableNMCRolesSupport
              ? []
              : [nmcLegacyReadOnlyRole],
            rolesToRemove: [tenantAdminRole, nmcLegacyTenantAdminRole],
          });

        default:
          throw new Error('unknown tenantSpecificTenantAdminRolesType');
      }
    };

  public readonly batchUnifiedRoles: IRolesEditorPolicy['batchUnifiedRoles'] =
    ({ roles, rolesToAdd, rolesToRemove }) => {
      const payload = {
        roles: new Map(roles),
      };

      for (const role of rolesToAdd) {
        payload.roles = this.addUnifiedRole(payload.roles, role.id);
      }

      for (const role of rolesToRemove) {
        payload.roles = this.removeUnifiedRole(payload.roles, role.id);
      }

      return payload.roles;
    };

  public readonly removeUnifiedRole: IRolesEditorPolicy['removeUnifiedRole'] = (
    targetUnifiedRolesMap,
    unifiedRoleIdToRemove,
  ) => {
    return this.payload.unifiedRolesEditor.removeRoleFromRolesMap({
      targetUnifiedRolesMap,
      unifiedRoleIdToRemove,
    });
  };

  public readonly removeAllUnifiedRoles: IRolesEditorPolicy['removeAllUnifiedRoles'] =
    (targetUnifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.removeAllRolesFromRolesMap({
        targetUnifiedRolesMap,
      });
    };

  public readonly addUnifiedRole: IRolesEditorPolicy['addUnifiedRole'] = (
    targetUnifiedRolesMap,
    unifiedRoleIdToAdd,
  ) => {
    return this.payload.unifiedRolesEditor.addRoleToRolesMap({
      targetUnifiedRolesMap,
      unifiedRoleIdToAdd,
      sourceUnifiedRolesCollection: this.payload.allAvailableUnifiedRoles,
    });
  };

  public readonly addAllUnifiedRoles: IRolesEditorPolicy['addAllUnifiedRoles'] =
    (targetUnifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.addAllRolesToRolesMap({
        targetUnifiedRolesMap,
        assignedRolesCollection: this.payload.unifiedRolesAssignedToResource,
        availableRolesCollection: this.payload.allAvailableUnifiedRoles,
      });
    };

  public readonly toUnifiedRoleCollectionDto: IRolesEditorPolicy['toUnifiedRoleCollectionDto'] =
    (unifiedRolesMap) => {
      return this.payload.unifiedRolesEditor.composeRoleCollectionDtoFromRolesMap(
        {
          unifiedRolesMap,
        },
      );
    };

  public readonly mapUserRoles: IRolesEditorPolicy['mapUserRoles'] = (
    unifiedRoleCollectionDto,
  ): TUnifiedRolesMap => {
    return this.payload.unifiedRolesEditor.composeRolesMapFromRoleCollectionDto(
      {
        unifiedRoleCollectionDto,
      },
    );
  };

  public readonly canUserPrincipalUpdateTenantAgnosticUnifiedRoles: IRolesEditorPolicy['canUserPrincipalUpdateTenantAgnosticUnifiedRoles'] =
    (userPrincipal) => {
      return userPrincipal.hasAnyOfPermissions([
        'system-admin:update:tenant-agnostic-user-role-assignments',
      ]);
    };

  public readonly canUserPrincipalUpdateTenantSpecificUnifiedRoles: IRolesEditorPolicy['canUserPrincipalUpdateTenantSpecificUnifiedRoles'] =
    (userPrincipal) => {
      return userPrincipal.hasAnyOfPermissions([
        'system-admin:update:tenant-specific-user-role-assignments',
        'SAVE_USER_ROLES',
      ]);
    };

  public readonly getAllAvailableUnifiedRoles: IRolesEditorPolicy['getAllAvailableUnifiedRoles'] =
    () => {
      return this.payload.allAvailableUnifiedRoles;
    };

  public readonly getPrevAssignedResourceUnifiedRoles: IRolesEditorPolicy['getPrevAssignedResourceUnifiedRoles'] =
    () => {
      return this.payload.unifiedRolesAssignedToResource;
    };

  public readonly hasUnifiedRoleAssigned: IRolesEditorPolicy['hasUnifiedRoleAssigned'] =
    (unifiedRoleToCheck) => {
      return !!this.payload.unifiedRolesAssignedToResource
        .toSerializedModel()
        .roles.find(({ id }) => id === unifiedRoleToCheck.id);
    };

  public readonly isRoleTenantAdminRelatedRole: IRolesEditorPolicy['isRoleTenantAdminRelatedRole'] =
    (maybeTenantAdminRelatedRole) => {
      return this.payload.unifiedRolesEditor.isRoleTenantAdminRelatedRole(
        maybeTenantAdminRelatedRole,
      );
    };

  public readonly compareUnifiedRolesState: IRolesEditorPolicy['compareUnifiedRolesState'] =
    (payload) => {
      return this.payload.unifiedRolesEditor.compareResourceUnifiedRolesState(
        payload,
      );
    };

  public readonly addAllNonTenantAdminRelatedRolesToAssignedRoles: IRolesEditorPolicy['addAllNonTenantAdminRelatedRolesToAssignedRoles'] =
    (assignedRoles) => {
      return this.payload.unifiedRolesEditor.addAllNonTenantAdminRelatedRolesToAssignedRoles(
        {
          targetUnifiedRolesMap: assignedRoles,
          assignedRolesCollection: this.payload.unifiedRolesAssignedToResource,
          availableRolesCollection: this.payload.allAvailableUnifiedRoles,
        },
      );
    };

  public removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
    assignedRoles: TUnifiedRolesMap,
  ) {
    return this.payload.unifiedRolesEditor.removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
      {
        targetUnifiedRolesMap: assignedRoles,
      },
    );
  }

  public readonly handleNMCRolesSupport: IRolesEditorPolicy['handleNMCRolesSupport'] =
    ({ tenantRoles, featureFlags = {} }) => {
      let result = tenantRoles;

      const { UNIFIED_ROLE_COLLECTION } = Enums.UiDtoFactoryEnum;
      const { create } = UIDtoFactory;
      const shouldDisableNMCRolesSupport =
        UnifiedRolesEditorResolver.shouldDisableNMCRolesSupport(featureFlags);

      if (shouldDisableNMCRolesSupport) {
        const nonNmcTenantRoles =
          new Array<SharedModels.UnifiedRoleViewModel>();

        for (const role of tenantRoles.roles) {
          if (isNumberString(role.id)) {
            nonNmcTenantRoles.push(role);
          }
        }

        result = { roles: nonNmcTenantRoles };
      }

      return create(UNIFIED_ROLE_COLLECTION, result).toSerializedModel();
    };

  public static readonly IAM_3542_DISABLE_UM_UI_NMC_ROLE_SUPPORT =
    'iam3542DisableUmUiNmcRoleSupport';
}
